import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { LighthouseScoreDisplay, getScoreColor } from "./LighthouseScoreDisplay";
import { LighthouseLoadingState } from "./LighthouseLoadingState";
import { LighthouseRecommendations } from "./LighthouseRecommendations";
import { CTABlock } from "./CTABlock";
import { runLighthouseAudit, type LighthouseAuditResult } from "@/lib/lighthouse.functions";

type Locale = "de" | "en";

type Props = { locale: Locale };

const RATE_LIMIT_KEY = "calv_psi_audits_v1";
const RATE_LIMIT_MAX = 3;
const RATE_LIMIT_WINDOW_MS = 24 * 60 * 60 * 1000;
const URL_MAX = 2000;

const PRIVATE_HOST_RE =
  /^(localhost|127\.|10\.|192\.168\.|169\.254\.|0\.0\.0\.0|::1|\[::1\]|fc00:|fd[0-9a-f]{2}:|fe80:)/i;

function validateUrlClient(raw: string): { ok: true; url: string } | { ok: false } {
  const trimmed = raw.trim();
  if (!trimmed || trimmed.length > URL_MAX) return { ok: false };
  let parsed: URL;
  try {
    parsed = new URL(trimmed.startsWith("http") ? trimmed : `https://${trimmed}`);
  } catch {
    return { ok: false };
  }
  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return { ok: false };
  if (PRIVATE_HOST_RE.test(parsed.hostname)) return { ok: false };
  if (!parsed.hostname.includes(".")) return { ok: false };
  if (/^\d+\.\d+\.\d+\.\d+$/.test(parsed.hostname)) return { ok: false };
  return { ok: true, url: parsed.toString() };
}

function readRateLimit(): number[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(RATE_LIMIT_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    const now = Date.now();
    return arr.filter((t: unknown) => typeof t === "number" && now - t < RATE_LIMIT_WINDOW_MS);
  } catch {
    return [];
  }
}

function writeRateLimit(stamps: number[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(stamps));
  } catch {
    /* ignore */
  }
}

const COPY = {
  de: {
    urlLabel: "URL der zu prüfenden Seite",
    placeholder: "https://example.com",
    submit: "Audit starten",
    submitting: "Audit läuft …",
    again: "Andere URL prüfen",
    quota: "Pro Browser sind drei Audits in 24 Stunden möglich.",
    privacy:
      "Die geprüfte URL wird intern zur Analyse gespeichert. Wir erheben keine IP-Adresse, keine E-Mail und keine weiteren personenbezogenen Daten.",
    quotaHit:
      "Limit erreicht: maximal drei Audits pro 24 Stunden. Bitte später erneut versuchen.",
    invalid: "Bitte eine gültige öffentliche http(s)-URL angeben.",
    fail: "Audit fehlgeschlagen. Die Seite ist möglicherweise nicht erreichbar oder der API-Service antwortet nicht.",
    auditedLabel: "Auditierte URL",
    stamp: (u: string, when: string) => `${u} · Lighthouse-Audit · ${when}`,
    ctaHead: <>Performance verbessern — <em>mit Calvarius.</em></>,
    ctaBody:
      "Lighthouse zeigt das Symptom. Wir zeigen die Lösung: Architektur, Tracking, Conversion. Unverbindlicher Erstkontakt in 24 h.",
    ctaPrimary: "Strategiegespräch anfragen",
    ctaSecondary: "Pillar-Beitrag lesen",
    contactTo: "/kontakt",
    pillarTo: "/blog/lighthouse-99-performance-core-web-vitals",
  },
  en: {
    urlLabel: "URL of the page to audit",
    placeholder: "https://example.com",
    submit: "Run audit",
    submitting: "Audit running …",
    again: "Check another URL",
    quota: "Three audits per browser per 24 hours.",
    privacy:
      "The tested URL is stored internally for analysis. We do not collect IP addresses, emails, or any other personal data.",
    quotaHit: "Limit reached: max. three audits per 24 hours. Please try again later.",
    invalid: "Please enter a valid public http(s) URL.",
    fail: "Audit failed. The site may be unreachable or the API service did not respond.",
    auditedLabel: "Audited URL",
    stamp: (u: string, when: string) => `${u} · Lighthouse audit · ${when}`,
    ctaHead: <>Improve performance — <em>with Calvarius.</em></>,
    ctaBody:
      "Lighthouse shows the symptom. We show the solution: architecture, tracking, conversion. Non-binding first contact within 24 h.",
    ctaPrimary: "Request strategy meeting",
    ctaSecondary: "Read the pillar article",
    contactTo: "/en/contact",
    pillarTo: "/en/blog/lighthouse-99-performance-core-web-vitals",
  },
} as const;

export function LighthouseChecker({ locale }: Props) {
  const c = COPY[locale];
  const auditFn = useServerFn(runLighthouseAudit);
  const [url, setUrl] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<LighthouseAuditResult | null>(null);

  function formatStamp(iso: string): string {
    try {
      const d = new Date(iso);
      return locale === "de"
        ? d.toLocaleString("de-DE", { dateStyle: "long", timeStyle: "short" })
        : d.toLocaleString("en-US", { dateStyle: "long", timeStyle: "short" });
    } catch {
      return iso;
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (honeypot.trim() !== "") return; // honeypot tripped

    const v = validateUrlClient(url);
    if (!v.ok) {
      setError(c.invalid);
      return;
    }

    const stamps = readRateLimit();
    if (stamps.length >= RATE_LIMIT_MAX) {
      setError(c.quotaHit);
      return;
    }

    setStatus("loading");
    setResult(null);

    try {
      const parsed = await auditFn({ data: { url: v.url, locale } });
      writeRateLimit([...stamps, Date.now()]);

      // Persist audit (non-blocking — user still sees results if this fails)
      try {
        const { supabase } = await import("@/integrations/supabase/client");
        const perf = parsed.scores[0]?.value ?? 0;
        const a11y = parsed.scores[1]?.value ?? 0;
        const bp = parsed.scores[2]?.value ?? 0;
        const seo = parsed.scores[3]?.value ?? 0;
        const vitalByAbbr = (abbr: string) =>
          parsed.vitals.find((x) => x.abbr === abbr)?.value ?? null;
        await supabase.from("lighthouse_audits").insert({
          tested_url: parsed.url,
          strategy: "mobile",
          score_performance: perf,
          score_accessibility: a11y,
          score_best_practices: bp,
          score_seo: seo,
          fcp_value: vitalByAbbr("FCP"),
          lcp_value: vitalByAbbr("LCP"),
          tbt_value: vitalByAbbr("TBT"),
          cls_value: vitalByAbbr("CLS"),
        });
      } catch (err) {
        console.error("Audit-Tracking failed", err);
      }

      setResult(parsed);
      setStatus("done");
    } catch (err) {
      console.error("Lighthouse audit failed", err);
      setStatus("error");
      setError(c.fail);
    }
  }

  function reset() {
    setStatus("idle");
    setResult(null);
    setError(null);
    setUrl("");
  }

  return (
    <div className="mx-auto max-w-3xl">
      {status !== "done" && (
        <form onSubmit={onSubmit} noValidate className="rounded-lg border border-rule bg-white p-6 md:p-8">
          <label htmlFor="lh-url" className="block font-mono text-xs uppercase tracking-[0.14em] text-ink-3">
            {c.urlLabel}
          </label>
          <input
            id="lh-url"
            type="url"
            inputMode="url"
            autoComplete="url"
            placeholder={c.placeholder}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            maxLength={URL_MAX}
            required
            disabled={status === "loading"}
            className="mt-2 block w-full rounded-md border border-rule bg-white px-4 py-3 text-base text-ink placeholder:text-ink-4 focus:border-cyan-deep focus:outline-none focus:ring-2 focus:ring-cyan-deep/30"
          />

          {/* Honeypot — hidden from humans, visible to naive bots */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              left: "-9999px",
              top: "-9999px",
              width: 1,
              height: 1,
              overflow: "hidden",
            }}
          >
            <label htmlFor="lh-website">Website</label>
            <input
              id="lh-website"
              name="website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
            />
          </div>

          <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
            <button
              type="submit"
              disabled={status === "loading"}
              className="inline-flex items-center gap-2 rounded-md bg-lime px-6 py-3 font-mono text-sm font-medium uppercase tracking-wider text-ink transition-colors hover:bg-lime/90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {status === "loading" ? c.submitting : `${c.submit} →`}
            </button>
            <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-ink-4">
              {c.quota}
            </span>
          </div>

          <p className="mt-3 text-xs text-ink-4">{c.privacy}</p>

          {error && (
            <p role="alert" className="mt-4 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
              {error}
            </p>
          )}
        </form>
      )}

      {status === "loading" && (
        <div className="mt-6">
          <LighthouseLoadingState locale={locale} />
        </div>
      )}

      {status === "done" && result && (
        <div className="mt-2">
          <div className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
            <div className="min-w-0">
              <div className="font-mono text-[11px] uppercase tracking-[0.14em] text-ink-4">
                {c.auditedLabel}
              </div>
              <div className="truncate text-sm text-ink-2">{result.url}</div>
            </div>
            <button
              type="button"
              onClick={reset}
              className="font-mono text-xs uppercase tracking-wider text-cyan-deep hover:underline"
            >
              {c.again} →
            </button>
          </div>

          <LighthouseScoreDisplay
            locale={locale}
            scores={result.scores.map((s) => ({ ...s, color: getScoreColor(s.value) }))}
            vitals={result.vitals}
            timestamp={c.stamp(result.url, formatStamp(result.fetchedAt))}
          />

          <LighthouseRecommendations items={result.recommendations} locale={locale} />

          <div className="mt-12">
            <CTABlock
              headline={c.ctaHead}
              body={c.ctaBody}
              primary={{
                label: c.ctaPrimary,
                to: c.contactTo,
                search: locale === "de"
                  ? { betreff: "strategiegespraech" }
                  : { subject: "strategy-meeting" },
              }}
              secondary={{ label: c.ctaSecondary, to: c.pillarTo }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
